import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import { FormattedMessage } from 'react-intl';

import {
  useTargetAccumulatorSelector,
  useTargetAccumulatorTileId,
} from '@/App/tiles/TargetAccumulator/TargetAccumulatorContext.tsx';
import type { DisplayMode } from '@/store/forwardAccumulator/state';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import { selectActiveTabZoomedTileId, setZoomedTileIdForActiveTab } from '@/store/tabs/slice.ts';
import { selectDisplayMode } from '@/store/targetAccumulator/selectors.ts';
import { targetAccumulatorSlice } from '@/store/targetAccumulator/slice.ts';

const RightColumnHeader = () => {
  const dispatch = useAppDispatch();

  const tileId = useTargetAccumulatorTileId();
  const displayMode = useAppSelector((state) => selectDisplayMode(state, tileId));
  const accuType = useTargetAccumulatorSelector(
    (targetAccumulator) => targetAccumulator?.properties.accuType ?? 'TargetAccu',
  );
  const customFields = useTargetAccumulatorSelector((targetAccumulator) => targetAccumulator?.properties.customFields);
  const isCustom = customFields && customFields.length > 0;
  const isZoomed = useAppSelector(selectActiveTabZoomedTileId) === tileId;
  const { sgwtWebAnalytics } = useSgwtWidgets();

  const setDisplayMode = (mode: DisplayMode) => {
    sgwtWebAnalytics?.trackEvent('Target Accumulator', 'Display mode changed', mode);
    dispatch(targetAccumulatorSlice.actions.targetAccumulatorSetDisplayMode({ tileId, displayMode: mode }));
  };

  function zoom() {
    sgwtWebAnalytics?.trackEvent('Target Accumulator', 'Zoomed in');
    dispatch(setZoomedTileIdForActiveTab({ tileId }));
  }

  return (
    <ul className="nav nav-compact-pills">
      <li className="nav-item me-auto">
        <span className="nav-link px-16px" data-nodrag="true" data-e2e="type-text">
          <FormattedMessage id={`targetAccumulator.form.accuType.${accuType}`} />
        </span>
      </li>
      <li className="nav-item">
        <button
          type="button"
          className={`nav-link ${displayMode === 'Pricer' ? 'active' : ''}`}
          tabIndex={-1}
          data-nodrag="true"
          data-e2e="pricer-mode"
          onClick={() => setDisplayMode('Pricer')}
        >
          Pricer
        </button>
      </li>
      <li className="d-flex align-items-center gap-4px nav-item">
        <button
          type="button"
          className={`nav-link ${displayMode === 'Scheduler' ? 'active' : ''}`}
          tabIndex={-1}
          data-nodrag="true"
          data-e2e="scheduler-mode"
          onClick={() => setDisplayMode('Scheduler')}
        >
          Scheduler
        </button>
        {isCustom && (
          <span className="text-info" data-e2e="fixing-edited-text">
            <FormattedMessage id="targetAccumulator.customFields.edited" />
          </span>
        )}
      </li>
      {!isZoomed && (
        <li className="nav-item pe-2">
          <button
            type="button"
            className="btn btn-flat-secondary btn-icon h-100 text-nowrap"
            onClick={zoom}
            data-e2e="zoom-btn"
          >
            <i className="icon">zoom_out_map</i>
          </button>
        </li>
      )}
    </ul>
  );
};

export default RightColumnHeader;
